/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '@framework/api-client/client/apiClient.ts';
import type { ErrorType } from '@framework/api-client/client/apiClient.ts';
import type { AlboRecordFoDTO } from '../schemas/alboRecordFoDTO.ts';
import type { GetAlboRecordParams } from '../schemas/getAlboRecordParams.ts';
import type { PostAlboRecordCsvParams } from '../schemas/postAlboRecordCsvParams.ts';
import type { PostAlboRecordPdfParams } from '../schemas/postAlboRecordPdfParams.ts';
import type { WsProblema } from '../schemas/wsProblema.ts';
import type { WsRispostaPaginataListAlboRecordFoDTO } from '../schemas/wsRispostaPaginataListAlboRecordFoDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce la lista, filtrata e ordinata dei Record Albo in formato PDF
 * @summary Export PDF Record Albo
 */
export const postAlboRecordPdf = (
  postAlboRecordPdfBody: string[],
  params: PostAlboRecordPdfParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<Blob>(
    {
      url: `/framework-cmp-domande/albo-record/pdf`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: postAlboRecordPdfBody,
      params,
      responseType: 'blob',
    },
    options,
  );
};

export const getPostAlboRecordPdfMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAlboRecordPdf>>,
    TError,
    { data: string[]; params: PostAlboRecordPdfParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAlboRecordPdf>>,
  TError,
  { data: string[]; params: PostAlboRecordPdfParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAlboRecordPdf>>,
    { data: string[]; params: PostAlboRecordPdfParams }
  > = props => {
    const { data, params } = props ?? {};

    return postAlboRecordPdf(data, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAlboRecordPdfMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAlboRecordPdf>>
>;
export type PostAlboRecordPdfMutationBody = string[];
export type PostAlboRecordPdfMutationError = ErrorType<WsProblema>;

/**
 * @summary Export PDF Record Albo
 */
export const usePostAlboRecordPdf = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAlboRecordPdf>>,
    TError,
    { data: string[]; params: PostAlboRecordPdfParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostAlboRecordPdfMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce la lista, filtrata e ordinata dei Record Albo in formato CSV
 * @summary Export CSV Record Albo
 */
export const postAlboRecordCsv = (
  postAlboRecordCsvBody: string[],
  params: PostAlboRecordCsvParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<Blob>(
    {
      url: `/framework-cmp-domande/albo-record/csv`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: postAlboRecordCsvBody,
      params,
      responseType: 'blob',
    },
    options,
  );
};

export const getPostAlboRecordCsvMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAlboRecordCsv>>,
    TError,
    { data: string[]; params: PostAlboRecordCsvParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAlboRecordCsv>>,
  TError,
  { data: string[]; params: PostAlboRecordCsvParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAlboRecordCsv>>,
    { data: string[]; params: PostAlboRecordCsvParams }
  > = props => {
    const { data, params } = props ?? {};

    return postAlboRecordCsv(data, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAlboRecordCsvMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAlboRecordCsv>>
>;
export type PostAlboRecordCsvMutationBody = string[];
export type PostAlboRecordCsvMutationError = ErrorType<WsProblema>;

/**
 * @summary Export CSV Record Albo
 */
export const usePostAlboRecordCsv = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAlboRecordCsv>>,
    TError,
    { data: string[]; params: PostAlboRecordCsvParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostAlboRecordCsvMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce una lista, filtrata, ordinata e paginata di oggetti Record Albo
 * @summary Ricerca Record Albo
 */
export const getAlboRecord = (
  params: GetAlboRecordParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListAlboRecordFoDTO>(
    {
      url: `/framework-cmp-domande/albo-record`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetAlboRecordQueryKey = (params: GetAlboRecordParams) =>
  [`/framework-cmp-domande/albo-record`, ...(params ? [params] : [])] as const;

export const getGetAlboRecordQueryOptions = <
  TData = Awaited<ReturnType<typeof getAlboRecord>>,
  TError = ErrorType<unknown>,
>(
  params: GetAlboRecordParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAlboRecord>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getAlboRecord>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAlboRecordQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAlboRecord>>> = ({
    signal,
  }) => getAlboRecord(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetAlboRecordQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAlboRecord>>
>;
export type GetAlboRecordQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca Record Albo
 */
export const useGetAlboRecord = <
  TData = Awaited<ReturnType<typeof getAlboRecord>>,
  TError = ErrorType<unknown>,
>(
  params: GetAlboRecordParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAlboRecord>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAlboRecordQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce un oggetto iscrizione all’albo identificato per id
 * @summary Cerca Record Albo tramite id
 */
export const getAlboRecordId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AlboRecordFoDTO>(
    { url: `/framework-cmp-domande/albo-record/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetAlboRecordIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/albo-record/${id}`] as const;

export const getGetAlboRecordIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getAlboRecordId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAlboRecordId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getAlboRecordId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAlboRecordIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAlboRecordId>>> = ({
    signal,
  }) => getAlboRecordId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetAlboRecordIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAlboRecordId>>
>;
export type GetAlboRecordIdQueryError = ErrorType<unknown>;

/**
 * @summary Cerca Record Albo tramite id
 */
export const useGetAlboRecordId = <
  TData = Awaited<ReturnType<typeof getAlboRecordId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAlboRecordId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAlboRecordIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce un oggetto iscrizione all’albo identificato per codice
 * @summary Cerca Record Albo tramite codice
 */
export const getAlboRecordCodiceCodice = (
  codice: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AlboRecordFoDTO>(
    {
      url: `/framework-cmp-domande/albo-record/codice/${codice}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetAlboRecordCodiceCodiceQueryKey = (codice: string) =>
  [`/framework-cmp-domande/albo-record/codice/${codice}`] as const;

export const getGetAlboRecordCodiceCodiceQueryOptions = <
  TData = Awaited<ReturnType<typeof getAlboRecordCodiceCodice>>,
  TError = ErrorType<unknown>,
>(
  codice: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAlboRecordCodiceCodice>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getAlboRecordCodiceCodice>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAlboRecordCodiceCodiceQueryKey(codice);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAlboRecordCodiceCodice>>
  > = ({ signal }) => getAlboRecordCodiceCodice(codice, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!codice, ...queryOptions };
};

export type GetAlboRecordCodiceCodiceQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAlboRecordCodiceCodice>>
>;
export type GetAlboRecordCodiceCodiceQueryError = ErrorType<unknown>;

/**
 * @summary Cerca Record Albo tramite codice
 */
export const useGetAlboRecordCodiceCodice = <
  TData = Awaited<ReturnType<typeof getAlboRecordCodiceCodice>>,
  TError = ErrorType<unknown>,
>(
  codice: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAlboRecordCodiceCodice>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAlboRecordCodiceCodiceQueryOptions(
    codice,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
